import { useCallback, useEffect, useRef, useState } from 'react';
import { zaehleOffeneMaterialMeldungen } from '../services/material/materialService';

/**
 * Zahl der offenen Materialmeldungen für das Abzeichen am Menüpunkt
 * „Material“ (docs/materialverwaltung-plan.md, Client 4).
 *
 * Drei Wege halten die Zahl frisch, ohne Realtime:
 *   * beim Start (sobald `aktiv` wird),
 *   * alle fünf Minuten, solange das Fenster sichtbar ist,
 *   * wenn das Fenster den Fokus bekommt oder wieder sichtbar wird —
 *     höchstens alle 30 s, denn Fokus und Sichtbarkeit kommen oft zusammen.
 * Dazu meldet der Material-Bereich selbst (`aktualisiere`), sobald er nach
 * „Bestellt“/„Aufgefüllt“ neu gezählt hat; eine ältere Antwort, die danach
 * eintrifft, überschreibt das nicht.
 *
 * `aktiv` ist das Recht auf die Ansicht (`ansichtErlaubt('material', …)`):
 * ohne Recht wird nichts gefragt, und das Abzeichen steht auf 0. Fail-open:
 * `null` (Fehler, Tabelle fehlt) hält den letzten Wert.
 */

/** Takt für das Nachzählen. */
export const MATERIAL_TAKT_MS = 5 * 60 * 1000;
/** Mindestabstand zwischen zwei Ladungen aus Fokus oder Sichtbarkeit. */
export const MATERIAL_FOKUS_ABSTAND_MS = 30 * 1000;

export type MaterialLadeAnlass = 'start' | 'takt' | 'fokus';

/**
 * Soll jetzt gezählt werden? Der Start immer; Takt und Fokus nur bei
 * sichtbarem Fenster (ein Tab im Hintergrund kostet sonst alle fünf Minuten
 * eine Anfrage, auf dem Handy Datenvolumen); Fokus zusätzlich mit Abstand.
 */
export const sollMaterialLaden = (a: {
    anlass: MaterialLadeAnlass;
    sichtbar: boolean;
    jetzt: number;
    /** Beginn der letzten Ladung; null = noch keine. */
    zuletzt: number | null;
}): boolean => {
    if (a.anlass === 'start') return true;
    if (!a.sichtbar) return false;
    if (a.anlass === 'takt' || a.zuletzt === null) return true;
    return a.jetzt - a.zuletzt >= MATERIAL_FOKUS_ABSTAND_MS;
};

/**
 * Neuer Stand des Abzeichens aus einer Antwort. Unbekannt (`null`) oder
 * keine Zahl: der alte Wert bleibt. Sonst ganzzahlig und nie negativ.
 */
export const materialAnzahlAusAntwort = (bisher: number, antwort: number | null | undefined): number => {
    if (typeof antwort !== 'number' || !Number.isFinite(antwort)) return bisher;
    return Math.max(0, Math.trunc(antwort));
};

const fensterSichtbar = (): boolean =>
    typeof document === 'undefined' || document.visibilityState !== 'hidden';

export interface MaterialOffen {
    anzahl: number;
    /** Der Material-Bereich hat neu gezählt — diese Zahl gilt ab sofort. */
    aktualisiere: (n: number) => void;
}

export function useMaterialOffen(aktiv: boolean): MaterialOffen {
    const [anzahl, setAnzahl] = useState(0);
    // Jede Ladung und jede Meldung aus dem Bereich zählt hoch; eine Antwort,
    // deren Anfrage vor einer neueren begann, gilt nicht mehr.
    const folgeRef = useRef(0);
    const zuletztRef = useRef<number | null>(null);

    useEffect(() => {
        if (!aktiv) return;
        let lebt = true;
        const lade = (anlass: MaterialLadeAnlass) => {
            const jetzt = Date.now();
            if (!sollMaterialLaden({ anlass, sichtbar: fensterSichtbar(), jetzt, zuletzt: zuletztRef.current })) return;
            zuletztRef.current = jetzt;
            const folge = ++folgeRef.current;
            zaehleOffeneMaterialMeldungen()
                .then((n) => {
                    if (lebt && folge === folgeRef.current) setAnzahl((bisher) => materialAnzahlAusAntwort(bisher, n));
                })
                .catch(() => undefined);
        };
        lade('start');
        const takt = window.setInterval(() => lade('takt'), MATERIAL_TAKT_MS);
        const beiFokus = () => lade('fokus');
        const beiSichtbarkeit = () => { if (fensterSichtbar()) lade('fokus'); };
        window.addEventListener('focus', beiFokus);
        document.addEventListener('visibilitychange', beiSichtbarkeit);
        return () => {
            lebt = false;
            window.clearInterval(takt);
            window.removeEventListener('focus', beiFokus);
            document.removeEventListener('visibilitychange', beiSichtbarkeit);
            // Recht weg oder abgemeldet: der Nächste erbt die Zahl nicht.
            // Eine Antwort, die noch unterwegs ist, hält `lebt` auf.
            zuletztRef.current = null;
            setAnzahl(0);
        };
    }, [aktiv]);

    const aktualisiere = useCallback((n: number) => {
        folgeRef.current++;
        zuletztRef.current = Date.now();
        setAnzahl((bisher) => materialAnzahlAusAntwort(bisher, n));
    }, []);

    return { anzahl: aktiv ? anzahl : 0, aktualisiere };
}
